import { Sequence, ConstVar, ast, Func, Var } from '../../ast/declare.js';
import { findColdDspGroupFromSink } from '../precompile/dsp-groups.js';

const dependencies = ({ precompiledCode }) => precompiledCode.dependencies.ast;
const nodeImplementationsCoreAndStateClasses = ({ precompiledCode: { nodeImplementations }, }) => Sequence(Object.values(nodeImplementations).map((precompiledImplementation) => [
    precompiledImplementation.stateClass,
    precompiledImplementation.core,
]));
const nodeStateInstances = ({ precompiledCode: { graph, nodes, nodeImplementations }, }) => Sequence([
    graph.fullTraversal.reduce((declarations, nodeId) => {
        const precompiledNode = nodes[nodeId];
        const precompiledNodeImplementation = nodeImplementations[precompiledNode.nodeType];
        if (!precompiledNode.state) {
            return declarations;
        }
        else {
            if (!precompiledNodeImplementation.stateClass) {
                throw new Error(`Node "${nodeId}" of type ${precompiledNode.nodeType} has a state but no state class`);
            }
            return [
                ...declarations,
                ConstVar(precompiledNodeImplementation.stateClass.name, precompiledNode.state.name, ast `{
                                ${Object.entries(precompiledNode.state.initialization).map(([key, value]) => ast `${key}: ${value},`)}
                            }`),
            ];
        }
    }, []),
]);
const nodeInitializations = ({ precompiledCode: { graph, nodes }, }) => Sequence([
    graph.fullTraversal.map((nodeId) => nodes[nodeId].initialization),
]);
const ioMessageReceivers = ({ globals: { msg }, precompiledCode: { io }, }) => Sequence(Object.values(io.messageReceivers).map((inletsMap) => {
    return Object.values(inletsMap).map((precompiledIoMessageReceiver) => {
        // prettier-ignore
        return Func(precompiledIoMessageReceiver.functionName, [
            Var(msg.Message, `m`)
        ], 'void') `
                    ${precompiledIoMessageReceiver.getSinkFunctionName()}(m)
                `;
    });
}));
const ioMessageSenders = ({ precompiledCode }, generateIoMessageSender) => Sequence(Object.entries(precompiledCode.io.messageSenders).map(([nodeId, portletIdsMap]) => Object.entries(portletIdsMap).map(([outletId, messageSender]) => {
    return generateIoMessageSender(messageSender.functionName, nodeId, outletId);
})));
const portletsDeclarations = ({ globals: { msg }, precompiledCode: { graph, nodes }, settings: { debug }, }) => Sequence([
    graph.fullTraversal
        .map((nodeId) => [nodes[nodeId], nodeId])
        .map(([precompiledNode, nodeId]) => [
        // 1. Declares signal outlets
        Object.values(precompiledNode.signalOuts).map((outName) => Var(`Float`, outName, `0`)),
        // 2. Declares message receivers for all message inlets.
        Object.entries(precompiledNode.messageReceivers).map(([inletId, astFunc]) => {
            // prettier-ignore
            return Func(astFunc.name, astFunc.args, astFunc.returnType) `
                            ${astFunc.body}
                            throw new Error('Node "${nodeId}", inlet "${inletId}", unsupported message : ' + ${msg.display}(${astFunc.args[0].name})${debug
                ? " + '\\nDEBUG : remember, you must return from message receiver'"
                : ''})
                        `;
        }),
    ]),
    // 3. Declares message senders for all message outlets.
    // This needs to come after all message receivers are declared since we reference them here.
    graph.fullTraversal
        .flatMap((nodeId) => Object.values(nodes[nodeId].messageSenders))
        // If only one sink declared, we don't need to declare the messageSender,
        // as precompilation takes care of substituting the messageSender name
        // with the sink name.
        .filter(({ sinkFunctionNames }) => sinkFunctionNames.length > 0)
        .map(({ messageSenderName, sinkFunctionNames }) => 
    // prettier-ignore
    Func(messageSenderName, [
        Var(msg.Message, `m`)
    ], 'void') `
                        ${sinkFunctionNames.map(functionName => `${functionName}(m)`)}
                    `),
]);
const dspLoop = ({ globals: { core, commons }, precompiledCode: { nodes, graph: { hotDspGroup, coldDspGroups }, }, }) => 
// prettier-ignore
ast `
        for (${core.IT_FRAME} = 0; ${core.IT_FRAME} < ${core.BLOCK_SIZE}; ${core.IT_FRAME}++) {
            ${commons._emitFrame}(${core.FRAME})
            ${hotDspGroup.traversal.map((nodeId) => [
    // For all inlets dsp functions, we render those that are not
    // the sink of a cold dsp group.
    ...Object.entries(nodes[nodeId].dsp.inlets)
        .filter(([inletId]) => findColdDspGroupFromSink(coldDspGroups, {
        nodeId,
        portletId: inletId
    }) === undefined)
        .map(([_, astElement]) => astElement),
    nodes[nodeId].dsp.loop
])}
            ${core.FRAME}++
        }
    `;
const coldDspInitialization = ({ globals: { msg }, precompiledCode: { graph }, }) => Sequence(Object.values(graph.coldDspGroups).map(({ functionName }) => `${functionName}(${msg.EMPTY_MESSAGE})`));
const coldDspFunctions = ({ globals: { msg }, precompiledCode: { graph: { coldDspGroups }, nodes, }, }) => Sequence(Object.values(coldDspGroups).map(({ dspGroup, sinkConnections: dspGroupSinkConnections, functionName, }) => 
// prettier-ignore
Func(functionName, [
    Var(msg.Message, `m`)
], 'void') `
                    ${dspGroup.traversal.map((nodeId) => nodes[nodeId].dsp.loop)}
                    ${dspGroupSinkConnections
    // For all sinks of the cold dsp group, we also render 
    // the inlets dsp functions that are connected to it. 
    .filter(([_, sink]) => sink.portletId in nodes[sink.nodeId].dsp.inlets)
    .map(([_, sink]) => nodes[sink.nodeId].dsp.inlets[sink.portletId])}
                `));
const importsExports = ({ precompiledCode: { dependencies } }, generateImport, generateExport) => Sequence([
    dependencies.imports.map(generateImport),
    dependencies.exports.map(generateExport),
]);
var templates = {
    dependencies,
    nodeImplementationsCoreAndStateClasses,
    nodeStateInstances,
    nodeInitializations,
    ioMessageReceivers,
    ioMessageSenders,
    portletsDeclarations,
    dspLoop,
    coldDspInitialization,
    coldDspFunctions,
    importsExports,
};

export { templates as default };
